import { useState, useRef, useEffect, useCallback } from 'react';
import { API } from '../config/api';

export function MentionInput({ value, onChange, onMentionsChange, placeholder, disabled, className, rows = 3 }) {
  const [staffList, setStaffList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filter, setFilter] = useState('');
  const [dropdownIndex, setDropdownIndex] = useState(0);
  const [cursorPos, setCursorPos] = useState(null);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);
  const mentionedIdsRef = useRef(new Set());

  // Fetch staff list once
  useEffect(() => {
    fetch(`${API}/users/staff?include_admins=true`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => setStaffList(data))
      .catch(() => {});
  }, []);

  const filtered = staffList.filter(s =>
    s.name.toLowerCase().includes(filter.toLowerCase())
  );

  const insertMention = useCallback((staff) => {
    if (!textareaRef.current || cursorPos === null) return;
    const before = value.slice(0, cursorPos);
    const after = value.slice(textareaRef.current.selectionStart);
    const atIdx = before.lastIndexOf('@');
    const newValue = before.slice(0, atIdx) + `@${staff.name} ` + after;
    onChange(newValue);

    // Track mentioned user
    mentionedIdsRef.current.add(staff.user_id);
    if (onMentionsChange) {
      onMentionsChange(Array.from(mentionedIdsRef.current));
    }

    setShowDropdown(false);
    setFilter('');
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = atIdx + staff.name.length + 2;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  }, [value, onChange, cursorPos, onMentionsChange]);

  const handleKeyDown = (e) => {
    if (!showDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setDropdownIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDropdownIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered.length > 0) {
      e.preventDefault();
      insertMention(filtered[dropdownIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleInput = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    onChange(val);

    // Sync mentionedIds — remove IDs whose @Name no longer appears in text
    const currentIds = new Set();
    for (const staff of staffList) {
      if (val.includes(`@${staff.name}`)) {
        if (mentionedIdsRef.current.has(staff.user_id)) {
          currentIds.add(staff.user_id);
        }
      }
    }
    if (currentIds.size !== mentionedIdsRef.current.size) {
      mentionedIdsRef.current = currentIds;
      if (onMentionsChange) onMentionsChange(Array.from(currentIds));
    }

    // Check if we're in a mention context
    const textBefore = val.slice(0, pos);
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch) {
      setFilter(atMatch[1]);
      setCursorPos(pos);
      setShowDropdown(true);
      setDropdownIndex(0);
    } else {
      setShowDropdown(false);
    }
  };

  // Reset mentions when value is cleared
  useEffect(() => {
    if (!value) {
      mentionedIdsRef.current = new Set();
      if (onMentionsChange) onMentionsChange([]);
    }
  }, [value, onMentionsChange]);

  // Close dropdown on outside click
  useEffect(() => {
    const close = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          textareaRef.current && !textareaRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="relative" data-testid="mention-input-wrapper">
      <textarea
        ref={textareaRef}
        data-testid="mention-textarea"
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        className={className}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        name="message-mention-input"
        style={{ backgroundColor: '#ffffff' }}
      />
      {showDropdown && filtered.length > 0 && (
        <div
          ref={dropdownRef}
          data-testid="mention-dropdown"
          className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50"
        >
          {filtered.map((staff, idx) => (
            <button
              key={staff.user_id}
              data-testid={`mention-option-${staff.user_id}`}
              type="button"
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                idx === dropdownIndex
                  ? 'bg-blue-50 text-blue-700'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(staff);
              }}
              onMouseEnter={() => setDropdownIndex(idx)}
            >
              <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-semibold shrink-0">
                {staff.name.charAt(0).toUpperCase()}
              </span>
              <span className="font-medium">{staff.name}</span>
              <span className="text-gray-400 text-xs ml-auto capitalize">{staff.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
